/**
 * Small fetch wrapper with a default timeout.
 * Prevents UI from hanging forever on stalled network requests.
 */
 
const DEFAULT_TIMEOUT_MS = 20000;
 
/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit & { timeoutMs?: number }} [init]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(input, init = {}) {
  const { timeoutMs, signal, ...rest } = init || {};
  const ms = Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_TIMEOUT_MS;
 
  // If AbortController isn't available (older browsers), fall back to plain fetch.
  if (typeof AbortController === 'undefined') {
    return fetch(input, { ...rest, signal });
  }
 
  const controller = new AbortController();
  let didTimeout = false;
  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, Math.max(0, ms));
 
  // If caller provided a signal, abort when either one aborts.
  if (signal) {
    if (signal.aborted) controller.abort();
    else {
      const onAbort = () => controller.abort();
      signal.addEventListener('abort', onAbort, { once: true });
    }
  }
 
  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } catch (err) {
    // Provide a clearer error for timeout-caused aborts (distinguish from user navigation abort).
    if (didTimeout && (err?.name === 'AbortError' || err?.code === 20)) {
      const e = new Error(`Request timed out after ${ms}ms`);
      e.name = 'TimeoutError';
      e.cause = err;
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

